import { useState, useEffect } from 'react';

interface ConsultationFormSectionProps {
  defaultPlan?: string;
}

export default function ConsultationFormSection({ defaultPlan = '' }: ConsultationFormSectionProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    plan: defaultPlan,
    propertyStatus: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<{ plan: string }>;
      setFormData((prev) => ({ ...prev, plan: custom.detail.plan }));
    };
    window.addEventListener('diagnosis-plan-selected', handler);
    return () => window.removeEventListener('diagnosis-plan-selected', handler);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('plan', formData.plan);
      body.append('propertyStatus', formData.propertyStatus);
      body.append('message', formData.message);

      const res = await fetch('https://readdy.ai/api/form/d6hqq9tpk1ctp7d9b80g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', plan: '', propertyStatus: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="consultation-form" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-emerald-50 text-emerald-700 text-sm font-semibold rounded-full mb-4">
            無料相談
          </span>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            無料相談を予約する
          </h2>
          <p className="text-lg text-slate-500">
            お気軽にご相談ください。2営業日以内にご返信いたします。
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
          {/* Top accent */}
          <div className="h-1.5 bg-gradient-to-r from-emerald-400 via-teal-500 to-emerald-400" />

          <div className="p-10 md:p-12">
            {submitStatus === 'success' ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-6">
                  <i className="ri-check-line text-4xl text-emerald-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">送信が完了しました！</h3>
                <p className="text-slate-500 mb-8">
                  ご相談内容を受け付けました。<br />
                  2営業日以内に担当者よりご連絡いたします。
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  もう一度送る
                </button>
              </div>
            ) : (
              <form
                id="pricing-consultation-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* お名前 */}
                  <div>
                    <label className="flex items-center justify-between text-xs font-bold text-slate-600 mb-2 uppercase tracking-wide">
                      <span>お名前</span>
                      <span className="bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full">必須</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="山田 太郎"
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all text-sm"
                    />
                  </div>

                  {/* メールアドレス */}
                  <div>
                    <label className="flex items-center justify-between text-xs font-bold text-slate-600 mb-2 uppercase tracking-wide">
                      <span>メールアドレス</span>
                      <span className="bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full">必須</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="example@email.com"
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all text-sm"
                    />
                  </div>

                  {/* 電話番号 */}
                  <div>
                    <label className="flex items-center justify-between text-xs font-bold text-slate-600 mb-2 uppercase tracking-wide">
                      <span>電話番号</span>
                      <span className="bg-slate-200 text-slate-500 text-[10px] px-2 py-0.5 rounded-full">任意</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="090-0000-0000"
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all text-sm"
                    />
                  </div>

                  {/* ご希望プラン */}
                  <div>
                    <label className="flex items-center justify-between text-xs font-bold text-slate-600 mb-2 uppercase tracking-wide">
                      <span>ご希望プラン</span>
                      <span className="bg-slate-200 text-slate-500 text-[10px] px-2 py-0.5 rounded-full">任意</span>
                    </label>
                    <select
                      name="plan"
                      value={formData.plan}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all text-sm cursor-pointer bg-white"
                    >
                      <option value="">選択してください</option>
                      <option value="開業サポート">開業サポート</option>
                      <option value="開業＋運営設計">開業＋運営設計</option>
                      <option value="運営代行">運営代行</option>
                      <option value="未定・相談したい">未定・相談したい</option>
                    </select>
                  </div>
                </div>

                {/* 物件の状況 */}
                <div>
                  <label className="flex items-center justify-between text-xs font-bold text-slate-600 mb-2 uppercase tracking-wide">
                    <span>物件の状況</span>
                    <span className="bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full">必須</span>
                  </label>
                  <select
                    name="propertyStatus"
                    value={formData.propertyStatus}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all text-sm cursor-pointer bg-white"
                  >
                    <option value="">選択してください</option>
                    <option value="まだ物件を持っていない">まだ物件を持っていない</option>
                    <option value="物件はあるが、まだ開業していない">物件はあるが、まだ開業していない</option>
                    <option value="すでに民泊を運営している">すでに民泊を運営している</option>
                  </select>
                </div>

                {/* ご相談内容 */}
                <div>
                  <label className="flex items-center justify-between text-xs font-bold text-slate-600 mb-2 uppercase tracking-wide">
                    <span>ご相談内容</span>
                    <div className="flex items-center gap-2">
                      <span className="bg-slate-200 text-slate-500 text-[10px] px-2 py-0.5 rounded-full">任意</span>
                      <span className={`text-[10px] ${formData.message.length > 500 ? 'text-red-500' : 'text-slate-400'}`}>
                        {formData.message.length}/500
                      </span>
                    </div>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={4}
                    placeholder="物件の場所・現在の状況・ご希望などをご自由にお書きください（500文字以内）"
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all resize-none text-sm"
                  />
                </div>

                {/* Error */}
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
                    <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0"></i>
                    <p className="text-sm text-red-700">送信に失敗しました。お手数ですが、もう一度お試しください。</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full flex items-center justify-center gap-3 py-4 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-base"
                >
                  <i className="ri-send-plane-fill text-lg"></i>
                  {isSubmitting ? '送信中...' : '無料相談を申し込む'}
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 font-semibold">または直接ご連絡</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                {/* WhatsApp / LINE */}
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href="https://wa.me/819012345678"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white transition-all hover:opacity-90 hover:-translate-y-0.5 shadow-md whitespace-nowrap cursor-pointer"
                    style={{ background: 'linear-gradient(135deg, #25D366, #1ebe5d)' }}
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    <span className="text-sm">WhatsApp</span>
                  </a>
                  <a
                    href="https://line.me/R/ti/p/@yourlineid"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white transition-all hover:opacity-90 hover:-translate-y-0.5 shadow-md whitespace-nowrap cursor-pointer"
                    style={{ background: 'linear-gradient(135deg, #06C755, #05b34d)' }}
                  >
                    <i className="ri-chat-smile-2-line text-xl"></i>
                    <span className="text-sm">LINE</span>
                  </a>
                </div>

                <p className="text-center text-xs text-slate-400">
                  ご相談は完全無料です。個人情報は厳重に管理いたします。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
